import {
  PRODUCT_DIMENSIONS, PRODUCT_KINDS, unitByCode, unitsOfDimension,
  type BatchRecordV1, type Dimension, type ProductDefineV1, type ProductReviseV1, type ProductUpdateV1, type SupplierDefineV1, type SupplierUpdateV1, type UnitDef,
} from '@gk/contracts'
import { fmtNum } from '../lib/format.ts'
import { unitsFor, unitSymbol, type BatchRow, type ProductKind, type ProductRow, type ProductVersionRow, type SupplierRow } from './catalog.ts'

/** Pure helpers of the catalogue page (M9): search and filter, expiry from a site-local day, formulation text <-> object, density, which units a product can be entered in, the
 *  checks the form makes early (the server makes them again: DENSITY_INVALID, DENSITY_SOURCE_REQUIRED, ...) and the payloads. Nothing here reads the database or the clock. */

// ---- search and filter ------------------------------------------------------------------------------------------------------------------
/** Case and accent insensitive text: "Électrique" and "electrique" are the same word. */
export const fold = (s: string): string => s.normalize('NFD').replace(/\p{M}+/gu, '').replace(/œ/gi, 'oe').replace(/æ/gi, 'ae').toLowerCase().trim()
const collate = (a: string, b: string): number => a.localeCompare(b, 'fr', { sensitivity: 'base' })

/** Every word typed must appear in the name or the reference ("huile 5w" finds "Huile moteur 5W-40"). An empty search matches everything. */
export function matchesQuery(p: { name: string; reference: string | null }, query: string): boolean {
  const words = fold(query).split(/\s+/).filter(Boolean)
  if (words.length === 0) return true
  const hay = fold(`${p.name} ${p.reference ?? ''}`)
  return words.every((w) => hay.includes(w))
}

export type KindFilter = ProductKind | 'all'
export const KIND_FILTERS: readonly KindFilter[] = ['all', ...PRODUCT_KINDS]
export interface ProductFilter { query: string; kind: KindFilter; showArchived: boolean; /** a product opened by a link stays listed even if archived */ pinnedId?: string | null }

/** The products to list: active ones first by name, archived ones (only when asked for) after them. */
export function filterProducts<T extends Pick<ProductRow, 'id' | 'name' | 'reference' | 'kind' | 'archived_at'>>(products: readonly T[], f: ProductFilter): T[] {
  return products
    .filter((p) => (f.showArchived || !p.archived_at || p.id === f.pinnedId) && (f.kind === 'all' || p.kind === f.kind) && matchesQuery(p, f.query))
    .sort((a, b) => Number(!!a.archived_at) - Number(!!b.archived_at) || collate(a.name, b.name))
}

export const sortSuppliers = <T extends Pick<SupplierRow, 'name' | 'archived_at'>>(rows: readonly T[]): T[] =>
  [...rows].sort((a, b) => Number(!!a.archived_at) - Number(!!b.archived_at) || collate(a.name, b.name))

// ---- days -------------------------------------------------------------------------------------------------------------------------------
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/
export function isDay(s: string): boolean {
  const m = DAY_RE.exec(s); if (!m) return false
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const t = new Date(Date.UTC(y, mo - 1, d))
  return t.getUTCFullYear() === y && t.getUTCMonth() === mo - 1 && t.getUTCDate() === d
}
/** A calendar day plus n days, as a day: pure calendar arithmetic (no time zone, no daylight saving). */
export function addDays(day: string, n: number): string {
  const m = DAY_RE.exec(day); if (!m) throw new Error(`not a day: ${day}`)
  const t = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + n))
  return `${String(t.getUTCFullYear()).padStart(4, '0')}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`
}
/** "2026-11-03" -> "03/11/2026" (a calendar day has no time zone to convert). */
export const dayText = (day: string): string => day.split('-').reverse().join('/')

export const SOON_DAYS = 30
export type ExpiryStatus = 'none' | 'expired' | 'soon' | 'ok'
/** `today` is the SITE's local day (lib/time localDate). Expired = before today (a batch is still good on the day written on it); soon = from today up to and including today + 30. */
export function expiryStatus(expiry: string | null | undefined, today: string, soonDays = SOON_DAYS): ExpiryStatus {
  if (!expiry) return 'none'
  if (expiry < today) return 'expired'
  return expiry <= addDays(today, soonDays) ? 'soon' : 'ok'
}
/** Batches the storekeeper must use first: the earliest expiry first, no expiry last. */
export const sortBatches = <T extends Pick<BatchRow, 'expiry' | 'lot'>>(rows: readonly T[]): T[] =>
  [...rows].sort((a, b) => (a.expiry ?? '9999-99-99').localeCompare(b.expiry ?? '9999-99-99') || collate(a.lot, b.lot))

// ---- versions, density ------------------------------------------------------------------------------------------------------------------
export const sortVersions = <T extends Pick<ProductVersionRow, 'version_no'>>(rows: readonly T[]): T[] => [...rows].sort((a, b) => b.version_no - a.version_no)

/** A density as the mirror carries it (a number, or the text of a numeric column), or null. */
export function densityOf(v: { density_kg_per_l: number | string | null } | null | undefined): number | null {
  const d = v?.density_kg_per_l
  if (d === null || d === undefined || d === '') return null
  const n = Number(d)
  return Number.isFinite(n) ? n : null
}
/** "0,87 kg/L" (the symbols come from the unit table: nothing typed twice). */
export const densityLabel = (d: number, lang: string): string => `${fmtNum(d, lang, 4)} ${unitSymbol('kg', lang)}/${unitSymbol('L', lang)}`
/** The number as a person types it: decimal comma in French, point in English. */
export const densityInput = (d: number | null, lang: string): string => (d === null ? '' : String(d).replace('.', lang.startsWith('en') ? '.' : ','))

export const DENSITY_MIN = 0.05
export const DENSITY_MAX = 25
export const DENSITY_DECIMALS = 4
export type DensityParse = { state: 'empty' } | { state: 'ok'; value: number } | { state: 'bad'; code: 'DENSITY_FORMAT' | 'DENSITY_DECIMALS' | 'DENSITY_RANGE' }
/** What was typed in the density field. Comma or point; at most four decimals (not rounded here: a rounded density would be a guess); between 0,05 and 25 kg/L, as the server decides. */
export function parseDensity(text: string): DensityParse {
  const s = text.replace(/[\s  ]/g, '')
  if (s === '') return { state: 'empty' }
  const m = /^(\d+)(?:[.,](\d+))?$/.exec(s)
  if (!m) return { state: 'bad', code: 'DENSITY_FORMAT' }
  const decimals = (m[2] ?? '').replace(/0+$/, '')
  if (decimals.length > DENSITY_DECIMALS) return { state: 'bad', code: 'DENSITY_DECIMALS' }
  const value = Number(decimals ? `${m[1]}.${decimals}` : m[1])
  if (value < DENSITY_MIN || value > DENSITY_MAX) return { state: 'bad', code: 'DENSITY_RANGE' }
  return { state: 'ok', value }
}
/** A density means something only for a product counted by mass or by volume (it is what lets one be entered in the other). */
export function densityApplies(unitCode: string): boolean {
  const d = unitByCode(unitCode)?.dimension
  return d === 'mass' || d === 'volume'
}

// ---- units ------------------------------------------------------------------------------------------------------------------------------
export interface ConverterOption { unit: UnitDef; /** reached only through the density (a mass for a volume or the reverse) */ viaDensity: boolean }
/** The units a quantity of this product may be typed in: `unitsFor` (its own dimension, plus the other of mass/volume only when the newest version has a density). The meter unit
 *  "cycles" (code `count`) is not a thing a product is counted in: left out unless it is the product's own unit. */
export function converterOptions(baseUnitCode: string, hasDensity: boolean): ConverterOption[] {
  const base = unitByCode(baseUnitCode)
  return unitsFor(baseUnitCode, hasDensity)
    .filter((u) => u.code !== 'count' || baseUnitCode === 'count')
    .map((unit) => ({ unit, viaDensity: !!base && unit.dimension !== base.dimension }))
    .sort((a, b) => Number(a.viaDensity) - Number(b.viaDensity))                          // the product's own units first (a stable sort: the table's order inside each group)
}

export interface UnitGroup { dimension: Dimension; units: UnitDef[] }
/** The base units a new product can have, grouped by what they measure (mass, volume, pieces, length). The meter unit `count` is not offered (pieces are `pc`). */
export const productUnitGroups = (): UnitGroup[] =>
  PRODUCT_DIMENSIONS.map((dimension) => ({ dimension, units: unitsOfDimension(dimension).filter((u) => u.code !== 'count') })).filter((g) => g.units.length > 0)
/** "kg (kg)", "pce (pc)", "m³ (m3)": the symbol and the code a person may have seen. */
export const unitOptionLabel = (u: UnitDef, lang: string): string => { const s = unitSymbol(u.code, lang); return s === u.code ? s : `${s} (${u.code})` }

// ---- formulation: key/value rows <-> object ---------------------------------------------------------------------------------------------
export interface FormulationRow { key: string; value: string; /** the stored value this row came from: kept when its text is left alone (a number stays a number) */ orig?: unknown }
export const blankRow = (): FormulationRow => ({ key: '', value: '' })
/** The text a stored value is edited as. */
export function valueText(v: unknown): string {
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  return v === null || v === undefined ? '' : JSON.stringify(v)
}
export const rowsOfFormulation = (f: Record<string, unknown> | null | undefined): FormulationRow[] => Object.entries(f ?? {}).map(([key, orig]) => ({ key, value: valueText(orig), orig }))
/** Rows back to an object. A fully blank row is dropped, a value left as it was keeps its type, anything else is the typed text. */
export function formulationOfRows(rows: readonly FormulationRow[]): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const r of rows) {
    const key = r.key.trim(); const text = r.value.trim()
    if (!key) continue
    out[key] = r.orig !== undefined && valueText(r.orig).trim() === text ? r.orig : text
  }
  return out
}
/** "N", "20": a pair for people. Numbers use the language's decimal mark. */
export function formulationPairs(f: Record<string, unknown> | null | undefined, lang: string): Array<{ key: string; value: string }> {
  return Object.entries(f ?? {}).map(([key, v]) => ({ key, value: typeof v === 'number' ? fmtNum(v, lang, 4) : valueText(v) }))
}
export const MAX_FORMULATION_CHARS = 2000      // the server's limit, on the stored text (jsonb prints ": " and ", ")
export function formulationSize(f: Record<string, unknown>): number {
  const parts = Object.entries(f).map(([k, v]) => `${JSON.stringify(k)}: ${JSON.stringify(v)}`)
  return `{${parts.join(', ')}}`.length
}
export function formulationEquals(a: Record<string, unknown> | null | undefined, b: Record<string, unknown> | null | undefined): boolean {
  const x = Object.entries(a ?? {}); const y = b ?? {}
  return x.length === Object.keys(y).length && x.every(([k, v]) => k in y && JSON.stringify(v) === JSON.stringify(y[k]))
}

// ---- the checks a form makes early -------------------------------------------------------------------------------------------------------
export const CATALOGUE_PROBLEMS = [
  'NAME_MISSING', 'UNIT_MISSING', 'DENSITY_FORMAT', 'DENSITY_DECIMALS', 'DENSITY_RANGE', 'DENSITY_SOURCE_MISSING', 'DENSITY_SOURCE_ALONE',
  'FORMULATION_KEY_MISSING', 'FORMULATION_VALUE_MISSING', 'FORMULATION_KEY_DUPLICATE', 'FORMULATION_TOO_LONG', 'NOTHING_CHANGED',
  'LOT_MISSING', 'LOT_EXISTS', 'EXPIRY_RANGE', 'RECEIVED_RANGE',
] as const
export type CatalogueProblem = (typeof CATALOGUE_PROBLEMS)[number]
/** Problems that only mean "not filled in yet": the button stays grey, no message is needed. */
export const EMPTY_ONLY: ReadonlySet<string> = new Set<CatalogueProblem>(['NAME_MISSING', 'UNIT_MISSING', 'LOT_MISSING', 'NOTHING_CHANGED'])

export interface VersionInput { density: string; source: string; rows: readonly FormulationRow[]; densityApplies: boolean }
export interface VersionContent { density: number | null; source: string | null; formulation: Record<string, unknown>; problems: CatalogueProblem[] }
/** The density (with its source) and the formulation of a version, read from what was typed. A density needs a source; both empty = no density. */
export function versionContent(i: VersionInput): VersionContent {
  const problems: CatalogueProblem[] = []
  let density: number | null = null; let source: string | null = null
  if (i.densityApplies) {
    const d = parseDensity(i.density); const s = i.source.trim()
    if (d.state === 'bad') problems.push(d.code)
    else if (d.state === 'ok') { density = d.value; if (s) source = s; else problems.push('DENSITY_SOURCE_MISSING') }
    else if (s) problems.push('DENSITY_SOURCE_ALONE')
  }
  const seen = new Set<string>()
  for (const r of i.rows) {
    const key = fold(r.key); const hasValue = r.value.trim() !== ''
    if (!key && !hasValue) continue
    if (!key) { if (!problems.includes('FORMULATION_KEY_MISSING')) problems.push('FORMULATION_KEY_MISSING'); continue }
    if (!hasValue && !problems.includes('FORMULATION_VALUE_MISSING')) problems.push('FORMULATION_VALUE_MISSING')
    if (seen.has(key) && !problems.includes('FORMULATION_KEY_DUPLICATE')) problems.push('FORMULATION_KEY_DUPLICATE')
    seen.add(key)
  }
  const formulation = formulationOfRows(i.rows)
  if (formulationSize(formulation) > MAX_FORMULATION_CHARS) problems.push('FORMULATION_TOO_LONG')
  return { density, source, formulation, problems }
}

export interface NewProductInput extends VersionInput { name: string; unit: string }
export function newProductProblems(i: NewProductInput): CatalogueProblem[] {
  const out: CatalogueProblem[] = []
  if (!i.name.trim()) out.push('NAME_MISSING')
  if (!i.unit) out.push('UNIT_MISSING')
  return [...out, ...versionContent({ ...i, densityApplies: i.densityApplies && !!i.unit }).problems]
}

/** A new version that says nothing new is not offered (versions are forever). */
export function reviseProblems(current: Pick<ProductVersionRow, 'density_kg_per_l' | 'density_source' | 'formulation'> | undefined, i: VersionInput): CatalogueProblem[] {
  const c = versionContent(i)
  if (c.problems.length > 0 || !current) return c.problems
  const same = densityOf(current) === c.density && (current.density_source?.trim() || null) === c.source && formulationEquals(current.formulation, c.formulation)
  return same ? ['NOTHING_CHANGED'] : []
}

export const MIN_DAY = '2000-01-01'
export const MAX_EXPIRY_DAYS = 3650
export interface BatchInput { lot: string; expiry: string; received: string }
/** The server's checks on a batch, said early: a lot is new for its product, an expiry after 2000 and within ten years, a delivery not in the future. */
export function batchProblems(i: BatchInput, existingLots: readonly string[], today: string): CatalogueProblem[] {
  const out: CatalogueProblem[] = []
  const lot = fold(i.lot)
  if (!lot) out.push('LOT_MISSING')
  else if (existingLots.some((l) => fold(l) === lot)) out.push('LOT_EXISTS')
  if (i.expiry && (!isDay(i.expiry) || i.expiry < MIN_DAY || i.expiry > addDays(today, MAX_EXPIRY_DAYS))) out.push('EXPIRY_RANGE')
  if (i.received && (!isDay(i.received) || i.received < MIN_DAY || i.received > today)) out.push('RECEIVED_RANGE')
  return out
}

// ---- payloads (the same shapes the contracts check) ---------------------------------------------------------------------------------------
const orNull = (s: string): string | null => (s.trim() === '' ? null : s.trim())

export interface NewProductArgs { productId: string; versionId: string; kind: ProductKind; name: string; unit: string; supplierId: string; reference: string; amm: string; content: VersionContent }
export function productDefinePayload(a: NewProductArgs): ProductDefineV1 {
  const reference = orNull(a.reference); const amm = a.kind === 'ppp' ? orNull(a.amm) : null
  return {
    product_id: a.productId, version_id: a.versionId, kind: a.kind, name: a.name.trim(), base_unit_code: a.unit,
    ...(a.supplierId ? { supplier_id: a.supplierId } : {}), ...(reference ? { reference } : {}), ...(amm ? { amm_number: amm } : {}),
    ...(a.content.density !== null ? { density_kg_per_l: a.content.density, density_source: a.content.source } : {}), formulation: a.content.formulation,
  }
}

export interface ProductEdit { name: string; supplierId: string; reference: string; amm: string }
/** Only what changed (an empty text clears a reference or number); an empty object means nothing to save. */
export function productChanges(p: Pick<ProductRow, 'kind' | 'name' | 'supplier_id' | 'reference' | 'amm_number'>, e: ProductEdit): ProductUpdateV1['set'] {
  const set: ProductUpdateV1['set'] = {}
  if (e.name.trim() && e.name.trim() !== p.name) set.name = e.name.trim()
  if ((e.supplierId || null) !== (p.supplier_id ?? null)) set.supplier_id = e.supplierId || null
  if (orNull(e.reference) !== orNull(p.reference ?? '')) set.reference = orNull(e.reference)
  if (p.kind === 'ppp' && orNull(e.amm) !== orNull(p.amm_number ?? '')) set.amm_number = orNull(e.amm)
  return set
}
export const productUpdatePayload = (p: Pick<ProductRow, 'id' | 'revision'>, set: ProductUpdateV1['set']): ProductUpdateV1 => ({ product_id: p.id, expected_revision: p.revision, set })

/** A revision replaces the whole content of the version in force: the density, its source and the formulation are all sent (the data sheet link is carried over). */
export function productRevisePayload(productId: string, versionId: string, newest: Pick<ProductVersionRow, 'version_no' | 'document_version_id'>, c: VersionContent): ProductReviseV1 {
  return {
    product_id: productId, version_id: versionId, expected_version_no: newest.version_no, density_kg_per_l: c.density, density_source: c.source, formulation: c.formulation,
    document_version_id: newest.document_version_id ?? null,
  }
}

export interface BatchArgs { batchId: string; productId: string; lot: string; expiry: string; received: string; supplierId: string }
export const batchRecordPayload = (a: BatchArgs): BatchRecordV1 => ({
  batch_id: a.batchId, product_id: a.productId, lot: a.lot.trim(), expiry: a.expiry || null, received_on: a.received || null, supplier_id: a.supplierId || null,
})

export const supplierDefinePayload = (supplierId: string, name: string, contact: string): SupplierDefineV1 => ({ supplier_id: supplierId, name: name.trim(), ...(orNull(contact) ? { contact: orNull(contact) } : {}) })
export function supplierChanges(s: Pick<SupplierRow, 'name' | 'contact'>, name: string, contact: string): SupplierUpdateV1['set'] {
  const set: SupplierUpdateV1['set'] = {}
  if (name.trim() && name.trim() !== s.name) set.name = name.trim()
  if (orNull(contact) !== orNull(s.contact ?? '')) set.contact = orNull(contact)
  return set
}
export const supplierUpdatePayload = (s: Pick<SupplierRow, 'id' | 'revision'>, set: SupplierUpdateV1['set']): SupplierUpdateV1 => ({ supplier_id: s.id, expected_revision: s.revision, set })
